import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import useRemoteData from '../hooks/useRemoteData';
import { createOrder, uploadImage, fetchPaymentSettings } from '../lib/api';
import { syncCustomerProfile } from '../lib/customerApi';
import { registerWithEmail } from '../lib/firebase';
import { writeTrackedOrder } from '../lib/trackedOrder';
import { getEnabledBankAccounts, isBankTransferAvailable } from '../config/paymentSettings';
import '../pages/auth.css';
import './checkout.css';

const EMPTY_PAYMENT_SETTINGS = { bank_transfer_enabled: false, bank_accounts: [] };
const DELIVERY_DETAILS_KEY = 'vemmac_delivery_details';
const PAYMENT_METHODS = ['Pay on delivery', 'Bank transfer', 'Card on delivery (POS)'];

const formatCurrency = (value) => `\u20a6${Number(value || 0).toLocaleString()}`;

const splitFullName = (fullName) => {
  const trimmed = String(fullName || '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
};

const combineName = (firstName, lastName, fallback = '') =>
  `${String(firstName || '').trim()} ${String(lastName || '').trim()}`.trim() ||
  String(fallback || '').trim();

const isProfileDeliveryComplete = (profile) =>
  !!(
    profile &&
    combineName(profile.first_name, profile.last_name) &&
    String(profile.phone || '').trim() &&
    String(profile.address || '').trim()
  );

const getAccountValue = (account, snakeKey, camelKey) =>
  String(account?.[snakeKey] || account?.[camelKey] || '').trim();

const defaultFormState = () => ({
  email: '',
  fullName: '',
  phone: '',
  address: '',
  landmark: '',
  extraNote: '',
  requestedDate: '',
  payment: 'Pay on delivery',
  saveDetails: false,
  password: '',
  confirmPassword: '',
});

const readCachedDeliveryDetails = () => {
  try {
    const raw = localStorage.getItem(DELIVERY_DETAILS_KEY);
    if (!raw) return defaultFormState();

    const parsed = JSON.parse(raw);
    return {
      ...defaultFormState(),
      email: parsed?.email || '',
      fullName: parsed?.fullName || parsed?.name || combineName(parsed?.firstName, parsed?.lastName),
      phone: parsed?.phone || '',
      address: parsed?.address || '',
      landmark: parsed?.landmark || '',
      extraNote: parsed?.extraNote || '',
      requestedDate: parsed?.requestedDate || '',
      payment: parsed?.payment || 'Pay on delivery',
    };
  } catch {
    return defaultFormState();
  }
};

const cacheDeliveryDetails = (details) => {
  try {
    localStorage.setItem(
      DELIVERY_DETAILS_KEY,
      JSON.stringify({
        email: details.email || '',
        fullName: details.fullName || '',
        phone: details.phone || '',
        address: details.address || '',
        landmark: details.landmark || '',
        extraNote: details.extraNote || '',
        requestedDate: details.requestedDate || '',
        payment: details.payment || 'Pay on delivery',
        updatedAt: new Date().toISOString(),
      })
    );
  } catch {
    // Ignore storage failures silently.
  }
};

function SuccessModal({ orderId, onClose }) {
  const navigate = useNavigate();
  const firstBtn = useRef(null);

  useEffect(() => {
    firstBtn.current?.focus();
  }, []);

  return (
    <div className="co-overlay" role="dialog" aria-modal="true" aria-labelledby="success-title">
      <div className="co-modal">
        <button type="button" className="co-modal-close" onClick={onClose} aria-label="Close">
          x
        </button>
        <p className="co-modal-status">Pending</p>
        <div className="co-check-wrap" aria-hidden="true">
          <svg className="co-check-svg" viewBox="0 0 52 52" fill="none">
            <circle className="co-check-circle" cx="26" cy="26" r="25" />
            <polyline className="co-check-tick" points="14,27 22,35 38,18" />
          </svg>
        </div>

        <h2 id="success-title" className="co-modal-title">
          Your order has been placed successfully.
        </h2>
        <p className="co-modal-subtitle">
          Your order is pending review.
        </p>
        <p className="co-modal-subtitle">
          A rider will contact you after your order has been reviewed.
        </p>
        <p className="co-modal-subtitle">
          You can track your order below.
        </p>
        {orderId && (
          <p className="co-order-id">Order ref: <span>{orderId}</span></p>
        )}

        <div className="co-modal-actions">
          {orderId && (
            <button
              ref={firstBtn}
              type="button"
              className="co-btn co-btn--primary"
              onClick={() => navigate(`/order-status/${orderId}`)}
            >
              Track Order
            </button>
          )}
          <button
            type="button"
            className="co-btn co-btn--outline"
            onClick={() => navigate('/menu')}
          >
            Back to menu
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentMethodModal({
  payment,
  onChange,
  onClose,
  onContinue,
  submitting,
  loadingPaymentSettings,
  bankTransferAvailable,
  error,
}) {
  return (
    <div className="co-overlay" role="dialog" aria-modal="true" aria-labelledby="payment-title">
      <div className="co-modal co-payment-modal">
        <button type="button" className="co-modal-close" onClick={onClose} aria-label="Close" disabled={submitting}>
          x
        </button>
        <h2 id="payment-title" className="co-modal-title">Select Payment Method</h2>
        <p className="co-modal-subtitle">Choose how you want to pay for this order.</p>

        <div className="co-payment-options co-payment-options--modal">
          {PAYMENT_METHODS.map((method) => {
            const isBankTransfer = method === 'Bank transfer';
            const isDisabled = isBankTransfer && !loadingPaymentSettings && !bankTransferAvailable;

            return (
              <label
                key={method}
                className={`co-payment-option${payment === method ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
              >
                <input
                  type="radio"
                  name="payment"
                  value={method}
                  checked={payment === method}
                  onChange={onChange}
                  className="co-payment-radio"
                  disabled={isDisabled || submitting}
                />
                <span className="co-payment-dot" />
                <span className="co-payment-label">
                  {method}
                  {isDisabled ? ' (unavailable)' : ''}
                </span>
              </label>
            );
          })}
        </div>

        {error && <p className="co-error" role="alert">{error}</p>}

        <div className="co-modal-actions">
          <button
            type="button"
            className="co-btn co-btn--primary"
            onClick={onContinue}
            disabled={submitting}
          >
            {submitting ? 'Please wait...' : 'Continue'}
          </button>
          <button
            type="button"
            className="co-btn co-btn--outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, totalAmount, clearCart } = useCart();
  const { isAuthenticated, profile, getToken, refreshProfile, saveProfile } = useAuth();
  const [placed, setPlaced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [paymentModalError, setPaymentModalError] = useState('');
  const [orderId, setOrderId] = useState('');
  const [form, setForm] = useState(() => readCachedDeliveryDetails());
  const [showDeliveryForm, setShowDeliveryForm] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState('');
  const [receiptUploadUrl, setReceiptUploadUrl] = useState('');
  const [receiptFileName, setReceiptFileName] = useState('');
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [showTransferGuide, setShowTransferGuide] = useState(false);
  const [copiedAccountId, setCopiedAccountId] = useState('');
  const {
    data: paymentSettings,
    loading: loadingPaymentSettings,
    error: paymentSettingsLoadError,
    reload: reloadPaymentSettings,
  } = useRemoteData(fetchPaymentSettings, EMPTY_PAYMENT_SETTINGS, {
    cacheKey: 'payment-settings',
    cacheStrategy: 'versioned',
    shape: 'object',
  });
  const [paymentSettingsError, setPaymentSettingsError] = useState('');
  const [selectedPaymentAccountId, setSelectedPaymentAccountId] = useState('');

  const bankAccounts = getEnabledBankAccounts(paymentSettings);
  const bankTransferAvailable = isBankTransferAvailable(paymentSettings);
  const hasSavedDeliveryDetails = isProfileDeliveryComplete(profile);
  const shouldShowDeliveryForm = !isAuthenticated || showDeliveryForm || !hasSavedDeliveryDetails;

  useEffect(() => {
    const cached = readCachedDeliveryDetails();
    setForm(cached);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !profile) return;

    const complete = isProfileDeliveryComplete(profile);
    setShowDeliveryForm(!complete);
    setForm((prev) => ({
      ...prev,
      email: profile.email || prev.email,
      fullName: combineName(profile.first_name, profile.last_name, prev.fullName),
      phone: profile.phone || prev.phone,
      address: profile.address || prev.address,
      landmark: profile.landmark || prev.landmark,
      saveDetails: false,
      password: '',
      confirmPassword: '',
    }));
  }, [isAuthenticated, profile]);

  useEffect(() => {
    if (!placed && cartItems.length === 0) {
      navigate('/cart', { replace: true });
    }
  }, [cartItems.length, placed, navigate]);

  useEffect(() => {
    if (paymentSettingsLoadError) {
      const message = paymentSettingsLoadError.message || 'Unable to load bank transfer settings.';
      setPaymentSettingsError(message);
      setPaymentModalError(message);
      return;
    }

    setPaymentSettingsError('');
    const accounts = getEnabledBankAccounts(paymentSettings);
    setSelectedPaymentAccountId((current) => {
      if (current && accounts.some((account) => account.id === current)) {
        return current;
      }
      return accounts[0]?.id || '';
    });
  }, [paymentSettings, paymentSettingsLoadError]);

  useEffect(() => {
    if (!loadingPaymentSettings && form.payment === 'Bank transfer' && !bankTransferAvailable) {
      setForm((prev) => ({ ...prev, payment: 'Pay on delivery' }));
    }
  }, [loadingPaymentSettings, bankTransferAvailable, form.payment]);

  const loadPaymentSettings = async () => {
    setPaymentSettingsError('');
    try {
      const settings = await fetchPaymentSettings();
      reloadPaymentSettings();
      return settings || EMPTY_PAYMENT_SETTINGS;
    } catch (error) {
      const message = error.message || 'Unable to load bank transfer settings.';
      setPaymentSettingsError(message);
      return null;
    }
  };

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validateCheckoutForm = () => {
    if (!form.fullName.trim()) {
      return 'Please enter your full name.';
    }
    if (!form.phone.trim()) {
      return 'Please enter your phone number.';
    }
    if (!form.address.trim()) {
      return 'Please enter your delivery address.';
    }
    if (!isAuthenticated && !form.email.trim()) {
      return 'Please enter your email address.';
    }
    if (!isAuthenticated && form.saveDetails) {
      if (!form.password) {
        return 'Please choose a password to save your details.';
      }
      if (form.password.length < 6) {
        return 'Password must be at least 6 characters.';
      }
      if (form.password !== form.confirmPassword) {
        return 'Passwords do not match.';
      }
    }
    return '';
  };

  const validateAndOpenPayment = (event) => {
    event.preventDefault();
    setSubmitError('');
    setPaymentModalError('');

    if (!cartItems.length) {
      setSubmitError('Your cart is empty. Add items before checking out.');
      return;
    }

    const validationMessage = validateCheckoutForm();
    if (validationMessage) {
      setSubmitError(validationMessage);
      if (isAuthenticated && !hasSavedDeliveryDetails) {
        setShowDeliveryForm(true);
      }
      return;
    }

    setShowPaymentModal(true);
  };

  const saveMissingAuthenticatedProfile = async (nameParts) => {
    if (!isAuthenticated || hasSavedDeliveryDetails) return;

    await saveProfile({
      email: profile?.email || '',
      first_name: nameParts.firstName,
      last_name: nameParts.lastName,
      phone: form.phone.trim(),
      address: form.address.trim(),
      landmark: form.landmark.trim(),
    });
    await refreshProfile();
  };

  const submitOrder = async ({ receiptImage, receiptName, paymentAccountId } = {}) => {
    setSubmitting(true);
    setSubmitError('');
    setPaymentModalError('');

    const validationMessage = validateCheckoutForm();
    if (validationMessage) {
      setSubmitError(validationMessage);
      setSubmitting(false);
      setShowPaymentModal(false);
      return;
    }

    const nameParts = splitFullName(form.fullName);

    try {
      let authToken = null;

      if (!isAuthenticated && form.saveDetails) {
        const credential = await registerWithEmail(form.email.trim(), form.password);
        authToken = await credential.user.getIdToken();
        await syncCustomerProfile(authToken, {
          email: form.email.trim(),
          first_name: nameParts.firstName,
          last_name: nameParts.lastName,
          phone: form.phone.trim(),
          address: form.address.trim(),
          landmark: form.landmark.trim(),
        });
        await refreshProfile();
      } else if (isAuthenticated) {
        authToken = await getToken();
        await saveMissingAuthenticatedProfile(nameParts);
      }

      const payload = {
        items: cartItems.map((item) => ({
          productId: item.productId || item.id,
          name: item.name,
          price: Number(item.price) || 0,
          quantity: Number(item.quantity) || 1,
          addOns: Array.isArray(item.addOnNames) ? item.addOnNames : item.addOns || [],
          details: item.details || null,
          type: item.type || 'dish',
        })),
        totalAmount,
        firstName: nameParts.firstName,
        lastName: nameParts.lastName,
        name: form.fullName.trim(),
        email: isAuthenticated ? profile?.email : form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        landmark: form.landmark.trim() || null,
        customerNote: form.extraNote.trim() || null,
        requestedDate: form.requestedDate || null,
        payment: form.payment,
        receiptImage: receiptImage || null,
        receiptName: receiptName || null,
        paymentAccountId: paymentAccountId || null,
        updateProfile: false,
      };

      const response = await createOrder(payload, authToken ? { token: authToken } : {});

      cacheDeliveryDetails({
        email: isAuthenticated ? profile?.email : form.email.trim(),
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        landmark: form.landmark.trim(),
        extraNote: form.extraNote.trim(),
        requestedDate: form.requestedDate,
        payment: form.payment,
      });

      const placedOrderId = response.orderId || response.id || '';
      if (placedOrderId) {
        writeTrackedOrder({
          id: placedOrderId,
          status: 'pending',
          payment_status: form.payment === 'Bank transfer' ? 'pending_verification' : 'pending',
          payment_method: form.payment,
        });
      }

      setOrderId(placedOrderId);
      clearCart();
      setPlaced(true);
      setShowPaymentModal(false);
      setShowTransferModal(false);
      setReceiptPreviewUrl('');
      setReceiptUploadUrl('');
      setReceiptFileName('');
    } catch (error) {
      const message = error.message || 'Unable to place your order right now. Please try again.';
      setSubmitError(message);
      setPaymentModalError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentContinue = async () => {
    setPaymentModalError('');

    if (form.payment === 'Bank transfer') {
      const settings = await loadPaymentSettings();
      if (!settings || !isBankTransferAvailable(settings)) {
        const message = 'Bank transfer is currently unavailable. Please choose another payment method.';
        setPaymentModalError(message);
        setSubmitError(message);
        return;
      }
      setShowPaymentModal(false);
      setShowTransferModal(true);
      return;
    }

    await submitOrder();
  };

  const handleReceiptChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSubmitError('');
    setUploadingReceipt(true);

    const previewUrl = URL.createObjectURL(file);
    setReceiptPreviewUrl(previewUrl);
    setReceiptFileName(file.name);

    try {
      const result = await uploadImage(file, 'receipts');
      setReceiptUploadUrl(result.url);
    } catch (error) {
      setReceiptPreviewUrl('');
      setReceiptUploadUrl('');
      setReceiptFileName('');
      setSubmitError(error.message || 'Unable to upload receipt. Please try again.');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleBankTransferDone = async () => {
    if (!bankTransferAvailable) {
      setSubmitError('Bank transfer is currently unavailable. Please choose another payment method.');
      return;
    }
    if (!selectedPaymentAccountId) {
      setSubmitError('Please select the bank account you paid into.');
      return;
    }
    if (!receiptUploadUrl) {
      setSubmitError(
        uploadingReceipt
          ? 'Receipt upload is still in progress. Please wait.'
          : 'Please attach your transfer receipt before continuing.'
      );
      return;
    }
    await submitOrder({
      receiptImage: receiptUploadUrl,
      receiptName: receiptFileName,
      paymentAccountId: selectedPaymentAccountId,
    });
  };

  const handleCopyAccountNumber = async (account) => {
    const accountNumber = getAccountValue(account, 'account_number', 'accountNumber');
    if (!accountNumber) {
      setSubmitError('Bank account number is not configured. Please contact support.');
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(accountNumber);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = accountNumber;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      setCopiedAccountId(account.id);
      window.setTimeout(() => setCopiedAccountId(''), 1500);
    } catch {
      setSubmitError('Could not copy account number. Please copy manually.');
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentModalError('');
  };

  const closeTransferModal = () => {
    if (submitting) return;
    setShowTransferModal(false);
    setSubmitError('');
  };

  return (
    <>
      {placed && (
        <SuccessModal
          orderId={orderId}
          onClose={() => setPlaced(false)}
        />
      )}

      {showPaymentModal && (
        <PaymentMethodModal
          payment={form.payment}
          onChange={onChange}
          onClose={closePaymentModal}
          onContinue={handlePaymentContinue}
          submitting={submitting}
          loadingPaymentSettings={loadingPaymentSettings}
          bankTransferAvailable={bankTransferAvailable}
          error={paymentModalError}
        />
      )}

      {showTransferModal && (
        <div className="co-overlay" role="dialog" aria-modal="true" aria-labelledby="transfer-title">
          <div className="co-modal co-transfer-modal">
            <div className="co-transfer-scroll">
              <h2 id="transfer-title" className="co-modal-title">Bank Transfer Details</h2>
              <p className="co-modal-subtitle">Make your transfer, upload receipt, then click Done.</p>

              <button
                type="button"
                className="co-transfer-guide-toggle"
                onClick={() => setShowTransferGuide((prev) => !prev)}
                aria-expanded={showTransferGuide}
              >
                <span>Read Before Making Payment</span>
                <span className={`co-guide-icon${showTransferGuide ? ' open' : ''}`} aria-hidden="true">v</span>
              </button>

              {showTransferGuide && (
                <ol className="co-transfer-guide-list">
                  <li>Review the account name before making payment. Ensure the account number matches the provided account name before transferring funds.</li>
                  <li>Upload a clear payment receipt after completing payment. The receipt should clearly show names, amount, date, and transfer time.</li>
                  <li>Do not upload old receipts or edited receipts. All receipts are reviewed before order approval.</li>
                </ol>
              )}

              <div className="co-transfer-details">
                {loadingPaymentSettings ? (
                  <p className="co-modal-subtitle">Loading bank transfer details...</p>
                ) : bankTransferAvailable ? (
                  bankAccounts.map((account) => {
                    const bankName = getAccountValue(account, 'bank_name', 'bankName');
                    const accountName = getAccountValue(account, 'account_name', 'accountName');
                    const accountNumber = getAccountValue(account, 'account_number', 'accountNumber');

                    return (
                      <label key={account.id} className={`co-transfer-account${selectedPaymentAccountId === account.id ? ' selected' : ''}`}>
                        <input
                          type="radio"
                          name="paymentAccount"
                          value={account.id}
                          checked={selectedPaymentAccountId === account.id}
                          onChange={() => setSelectedPaymentAccountId(account.id)}
                          className="co-payment-radio"
                        />
                        <div className="co-transfer-account-info">
                          <p><span>Bank Name</span><strong>{bankName || 'Not configured'}</strong></p>
                          <p><span>Account Name</span><strong>{accountName || 'Not configured'}</strong></p>
                          <p className="co-account-number-row">
                            <span>Account Number</span>
                            <strong>{accountNumber || 'Not configured'}</strong>
                            <button
                              type="button"
                              className="co-copy-btn"
                              onClick={(event) => {
                                event.preventDefault();
                                handleCopyAccountNumber(account);
                              }}
                              aria-label={`Copy ${bankName} account number`}
                              title="Copy account number"
                            >
                              {copiedAccountId === account.id ? 'OK' : 'Copy'}
                            </button>
                          </p>
                        </div>
                      </label>
                    );
                  })
                ) : (
                  <p className="co-error" role="alert">
                    {paymentSettingsError || 'Bank transfer is currently unavailable. Please choose another payment method.'}
                  </p>
                )}
              </div>

              <label className="co-receipt-upload">
                <span>{uploadingReceipt ? 'Uploading receipt...' : 'Attach transfer receipt'}</span>
                <input type="file" accept="image/*" onChange={handleReceiptChange} disabled={uploadingReceipt || submitting} />
              </label>

              {receiptPreviewUrl && (
                <div className="co-receipt-preview-wrap">
                  <p className="co-receipt-name">
                    {receiptFileName || 'Receipt attached'}
                    {!receiptUploadUrl && uploadingReceipt ? ' - uploading...' : ''}
                  </p>
                  <img src={receiptPreviewUrl} alt="Transfer receipt" className="co-receipt-preview" />
                </div>
              )}

              {submitError && <p className="co-error" role="alert">{submitError}</p>}
            </div>

            <div className="co-transfer-actions">
              <button
                type="button"
                className="co-btn co-btn--primary"
                onClick={handleBankTransferDone}
                disabled={submitting || uploadingReceipt || !receiptUploadUrl || !selectedPaymentAccountId}
              >
                {submitting ? 'Submitting...' : 'Done'}
              </button>
              <button
                type="button"
                className="co-btn co-btn--outline"
                onClick={closeTransferModal}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="co-page">
        <header className="co-header">
          <p className="co-eyebrow">Almost there</p>
          <h1 className="co-title">Checkout</h1>
          <p className="co-subtitle">Confirm your contact and delivery details before choosing payment.</p>
        </header>

        <div className="co-layout co-layout--single">
          <form className="co-form-card" onSubmit={validateAndOpenPayment} noValidate>
            <section className="co-section">
              <h2 className="co-section-title">Contact Information</h2>

              {!isAuthenticated && (
                <div className="co-field">
                  <label htmlFor="email" className="co-label">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className="co-input"
                    value={form.email}
                    onChange={onChange}
                    required
                    placeholder="you@example.com"
                  />
                </div>
              )}

              {isAuthenticated && (
                <div className="co-profile-email">
                  <span>Signed in as</span>
                  <strong>{profile?.email || 'your account'}</strong>
                </div>
              )}
            </section>

            <section className="co-section">
              <h2 className="co-section-title">Delivery Information</h2>

              {isAuthenticated && hasSavedDeliveryDetails && !shouldShowDeliveryForm ? (
                <div className="co-saved-delivery">
                  <div>
                    <p><strong>{combineName(profile.first_name, profile.last_name)}</strong></p>
                    <p>{profile.phone}</p>
                    <p>{profile.address}</p>
                    {profile.landmark && <p>Landmark: {profile.landmark}</p>}
                  </div>
                  <button
                    type="button"
                    className="co-btn co-btn--outline co-btn--compact"
                    onClick={() => setShowDeliveryForm(true)}
                  >
                    Update Delivery Details
                  </button>
                </div>
              ) : (
                <>
                  {isAuthenticated && !hasSavedDeliveryDetails && (
                    <p className="co-inline-note">Complete your delivery details before placing this order.</p>
                  )}

                  <div className="co-field">
                    <label htmlFor="fullName" className="co-label">Full Name</label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      autoComplete="name"
                      className="co-input"
                      value={form.fullName}
                      onChange={onChange}
                      required
                      placeholder="e.g. Amara Johnson"
                    />
                  </div>

                  <div className="co-field">
                    <label htmlFor="phone" className="co-label">Phone Number</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      className="co-input"
                      value={form.phone}
                      onChange={onChange}
                      required
                      placeholder="0801 234 5678"
                    />
                  </div>

                  <div className="co-field">
                    <label htmlFor="address" className="co-label">Address</label>
                    <textarea
                      id="address"
                      name="address"
                      autoComplete="street-address"
                      className="co-input co-textarea"
                      rows="3"
                      value={form.address}
                      onChange={onChange}
                      required
                      placeholder="Street, area, city - be precise so our rider can find you"
                    />
                  </div>

                  <div className="co-field">
                    <label htmlFor="landmark" className="co-label">Landmark (optional)</label>
                    <input
                      id="landmark"
                      name="landmark"
                      type="text"
                      className="co-input"
                      value={form.landmark}
                      onChange={onChange}
                      placeholder="Nearby landmark to help our rider find you"
                    />
                  </div>

                  <div className="co-field">
                    <label htmlFor="extraNote" className="co-label">Delivery Notes (optional)</label>
                    <textarea
                      id="extraNote"
                      name="extraNote"
                      className="co-input co-textarea"
                      rows="2"
                      value={form.extraNote}
                      onChange={onChange}
                      placeholder="Any extra delivery instructions or preferences"
                    />
                  </div>
                </>
              )}

              {!isAuthenticated && (
                <div className="co-save-details-block">
                  <label className="co-checkbox-row">
                    <input
                      type="checkbox"
                      name="saveDetails"
                      checked={form.saveDetails}
                      onChange={onChange}
                    />
                    <span className="co-checkbox-label">Save my details for future orders</span>
                  </label>

                  {form.saveDetails && (
                    <div className="co-account-fields">
                      <div className="co-field">
                        <label htmlFor="password" className="co-label">Password</label>
                        <input
                          id="password"
                          name="password"
                          type="password"
                          autoComplete="new-password"
                          className="co-input"
                          value={form.password}
                          onChange={onChange}
                          required={form.saveDetails}
                        />
                      </div>
                      <div className="co-field">
                        <label htmlFor="confirmPassword" className="co-label">Confirm Password</label>
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          autoComplete="new-password"
                          className="co-input"
                          value={form.confirmPassword}
                          onChange={onChange}
                          required={form.saveDetails}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="co-field co-requested-date">
                <label htmlFor="requestedDate" className="co-label">Preferred delivery date &amp; time (optional)</label>
                <input
                  id="requestedDate"
                  name="requestedDate"
                  type="datetime-local"
                  className="co-input"
                  value={form.requestedDate}
                  onChange={onChange}
                />
              </div>
            </section>

            {submitError && !showTransferModal && (
              <p className="co-error" role="alert">{submitError}</p>
            )}

            <div className="co-form-actions">
              <button
                type="submit"
                className="co-btn co-btn--primary co-btn--full"
                disabled={submitting}
              >
                Place Order
              </button>
              <Link to="/cart" className="co-back-link">&lt;- Back to cart</Link>
              <p className="co-total-inline">Order total: <strong>{formatCurrency(totalAmount)}</strong></p>
            </div>
          </form>
        </div>
      </main>
    </>
  );
};

export default CheckoutPage;
